import { useEffect } from 'react'
import {
  Filters,
  PlanStatusEnum,
  useGetPlanSummariesQuery,
  ErrorMessage,
  Mtds,
  templates,
  useGetMatricesQuery,
  useGetProductsQuery,
  useGetSampntTypes,
  useGetSubstancesQuery,
  useGetPurposesQuery,
  useGetSubPurposesQuery,
  widgets,
  pruneSubPlans,
  PlanSummary
} from 'prove-common'
import { filterTasksUiSchema } from '../../schemas/filter-tasks-ui-schema'
import { getFilterTasksSchema } from '../../schemas/filter-tasks-schema'
import {
  expandPlanIdsWithAncestors,
  hasSelectedPlanInChain
} from '../../utils/taskHelper'
import { Form, validator } from 'prove-common/rjsf'

type TaskFilterFormProps = {
  filters: Filters<PlanStatusEnum>
  setFilters: React.Dispatch<React.SetStateAction<Filters<PlanStatusEnum>>>
  isEnabled?: boolean
  uniquePlanIds: number[]
  uniqueProductIds: number[]
  uniqueMatrixIds: number[]
  uniqueSampntTypeIds: number[]
  uniqueSubstanceIds: number[]
  uniquePurposeIds: number[]
  uniqueSubPurposeIds: number[]
  onReadyChange?: (isReady: boolean) => void
}

export default function TaskFilterForm({
  filters,
  setFilters,
  isEnabled,
  uniquePlanIds,
  uniqueProductIds,
  uniqueMatrixIds,
  uniqueSampntTypeIds,
  uniqueSubstanceIds,
  uniquePurposeIds,
  uniqueSubPurposeIds,
  onReadyChange
}: Readonly<TaskFilterFormProps>) {
  const {
    data: plans,
    isPending: isPendingPlans,
    isError: isErrorPlans,
    isSuccess: isSuccessPlans,
    error: plansError
  } = useGetPlanSummariesQuery(isEnabled)
  const {
    data: products,
    isPending: isPendingProducts,
    isError: isErrorProducts,
    isSuccess: isSuccessProducts,
    error: productsError
  } = useGetProductsQuery(isEnabled)
  const {
    data: matrices,
    isPending: isPendingMatrices,
    isError: isErrorMatrices,
    isSuccess: isSuccessMatrices,
    error: matricesError
  } = useGetMatricesQuery(isEnabled)
  const {
    data: sampntTypes,
    isPending: isPendingSampntTypes,
    isError: isErrorSampntTypes,
    isSuccess: isSuccessSampntTypes,
    error: sampntTypesError
  } = useGetSampntTypes(isEnabled)
  const {
    data: substances,
    isPending: isPendingSubstancesQuery,
    isError: isErrorSubstances,
    isSuccess: isSuccessSubstances,
    error: substancesError
  } = useGetSubstancesQuery(isEnabled)
  const {
    data: purposes,
    isPending: isPendingPurposes,
    isError: isErrorPurposes,
    isSuccess: isSuccessPurposes,
    error: purposesError
  } = useGetPurposesQuery(isEnabled)
  const {
    data: subPurposes,
    isPending: isPendingSubPurposes,
    isError: isErrorSubPurposes,
    isSuccess: isSuccessSubPurposes,
    error: subPurposesError
  } = useGetSubPurposesQuery(isEnabled)

  const isPending =
    isPendingPlans ||
    isPendingProducts ||
    isPendingMatrices ||
    isPendingSampntTypes ||
    isPendingSubstancesQuery ||
    isPendingPurposes ||
    isPendingSubPurposes
  const isError =
    isErrorPlans ||
    isErrorProducts ||
    isErrorMatrices ||
    isErrorSampntTypes ||
    isErrorSubstances ||
    isErrorPurposes ||
    isErrorSubPurposes
  const isSuccess =
    isSuccessPlans &&
    isSuccessProducts &&
    isSuccessMatrices &&
    isSuccessSampntTypes &&
    isSuccessSubstances &&
    isSuccessPurposes &&
    isSuccessSubPurposes
  const error =
    plansError ??
    productsError ??
    matricesError ??
    sampntTypesError ??
    substancesError ??
    purposesError ??
    subPurposesError

  useEffect(() => {
    onReadyChange?.(isSuccess)
  }, [isSuccess, onReadyChange])

  const allPlans: PlanSummary[] = Array.isArray(plans) ? plans : []
  const activePlans: PlanSummary[] = allPlans.filter(
    (p) => p.status === 'active'
  )
  const rootPlans: PlanSummary[] = activePlans.filter(
    (plan: PlanSummary) => plan.parents?.length === 0
  )
  const selectedPlanIds = filters.planIds ?? []
  const selectedSubPlanIds = filters.subPlanIds ?? []

  const parentIdSet = new Set(
    selectedPlanIds.map(Number).filter((n) => !Number.isNaN(n))
  )
  const planById = new Map(allPlans.map((plan) => [plan.id, plan]))
  let subPlans: PlanSummary[] = []
  if (parentIdSet.size > 0) {
    // Collect every under-plan at any depth below the selected plan(s), not
    // just the direct children, so tasks on deep sub-plans stay reachable.
    subPlans = activePlans
      .filter((p) => (p.parents?.length ?? 0) > 0)
      .filter((p) => hasSelectedPlanInChain(p, parentIdSet, planById))
  } else if (selectedSubPlanIds.length > 0) {
    subPlans = activePlans.filter((p) =>
      selectedSubPlanIds.includes(p.id.toString())
    )
  }

  // Tasks sit on the plans they are directly attached to (often sub-plans).
  // Expand those ids with every ancestor so a top-level plan whose tasks all
  // live on sub-plans is still offered in the Plan/Underplan filter options.
  const relevantPlanIds = expandPlanIdsWithAncestors(uniquePlanIds, allPlans)

  // Filter option lists by the unique ids derived from tasks
  const filteredRootPlans = rootPlans.filter((val) =>
    relevantPlanIds.includes(val.id)
  )
  const filteredSubPlans = subPlans.filter((val) =>
    relevantPlanIds.includes(val.id)
  )
  const filteredProducts = (products ?? []).filter((val) =>
    uniqueProductIds.includes(val.id)
  )
  const filteredMatrices = (matrices ?? []).filter((val) =>
    uniqueMatrixIds.includes(val.id)
  )
  const filteredSampntTypes = (sampntTypes ?? []).filter((val) =>
    uniqueSampntTypeIds.includes(val.id)
  )
  const filteredSubstances = (substances ?? []).filter((val) =>
    uniqueSubstanceIds.includes(val.id)
  )
  const filteredPurposes = (purposes ?? []).filter((val) =>
    uniquePurposeIds.includes(val.id)
  )
  const filteredSubPurposes = (subPurposes ?? []).filter((val) =>
    uniqueSubPurposeIds.includes(val.id)
  )

  return (
    <Mtds.Grid>
      {isPending && !isError && (
        <Mtds.Spinner className='mt-8'>Henter filterdata...</Mtds.Spinner>
      )}
      {isError && <ErrorMessage error={error} />}
      {isSuccess && (
        <Form
          noHtml5Validate
          schema={getFilterTasksSchema({
            rootPlans: filteredRootPlans,
            subPlans: filteredSubPlans,
            products: filteredProducts,
            matrices: filteredMatrices,
            sampntTypes: filteredSampntTypes,
            substances: filteredSubstances,
            purposes: filteredPurposes,
            subPurposes: filteredSubPurposes
          })}
          uiSchema={filterTasksUiSchema}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={filters}
          onChange={({ formData }) => {
            const normalizeIds = (value: unknown): string[] => {
              if (Array.isArray(value)) {
                return value.map(String).filter((id) => id.length > 0)
              }
              if (value == null || value === '') return []
              if (typeof value === 'object') return []
              return [String(value)]
            }
            const planIds = normalizeIds(formData.planIds)
            const subPlanIds = normalizeIds(formData.subPlanIds)
            setFilters({
              ...filters,
              ...formData,
              planIds,
              subPlanIds: pruneSubPlans(planIds, subPlanIds, allPlans)
            })
          }}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
        >
          <></>
        </Form>
      )}
    </Mtds.Grid>
  )
}
